import { useTranslation } from "react-i18next";
import { Building2, CreditCard, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useModuleAccess } from "@/modules/useModuleAccess";
import type { AppointmentDetailsController } from "@/components/calendar/useAppointmentDetailsController";

const formatCurrency = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function AppointmentPaymentCard({ ctrl }: { ctrl: AppointmentDetailsController }) {
  const { t } = useTranslation("calendar");
  const { has } = useModuleAccess();
  if (!has("financial")) return null;
  const {
    appointmentBalanceTotal,
    servicesBalanceTotal,
    packageSuggestedChargeTotal,
    handleGoToPayment,
    canShowPaymentCta,
    hasActiveSale,
    saleByAppointment,
    isPaymentSectionSettled,
    onOpenChange,
    isCrossClinicAppointment,
    crossClinicName,
    switchClinic,
    appointment,
  } = ctrl;

  return (
    <section className="rounded-[18px] border border-border/30 bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-bold">{t("appointmentDetails.payment")}</span>
        {isPaymentSectionSettled ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-0">
            {t("financialSettled")}
          </Badge>
        ) : null}
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between text-muted-foreground">
          <span>{t("appointmentForm.productsAndServicesShort")}</span>
          <span>{formatCurrency(servicesBalanceTotal)}</span>
        </div>
        <div className="flex items-center justify-between text-muted-foreground">
          <span>{t("packages.title")}</span>
          <span>{formatCurrency(packageSuggestedChargeTotal)}</span>
        </div>
        <div className="flex items-center justify-between rounded-md bg-muted/60 px-3 py-2 font-semibold text-foreground">
          <span>{t("appointmentForm.totalToPay")}</span>
          <span>{formatCurrency(appointmentBalanceTotal)}</span>
        </div>

        {isCrossClinicAppointment && (hasActiveSale || canShowPaymentCta) ? (
          <div className="mt-1 space-y-2 rounded-[13px] border border-border bg-muted/40 p-3">
            <p className="text-sm text-muted-foreground">
              {t("multiClinic.crossClinicWarning", { name: crossClinicName })}
            </p>
            <Button
              variant="outline"
              className="h-[48px] w-full rounded-[13px]"
              disabled={switchClinic.isPending || !appointment?.clinic_id}
              onClick={() => {
                if (appointment?.clinic_id) switchClinic.mutate(appointment.clinic_id);
              }}
            >
              <Building2 className="size-[18px]" />
              {t("multiClinic.switchToClinic", { name: crossClinicName })}
            </Button>
          </div>
        ) : null}

        {!isCrossClinicAppointment && hasActiveSale && saleByAppointment ? (
          <PermissionGuard resource="sales" action="view" showAlert={false}>
            <Button asChild variant="outline" className="mt-1 h-[48px] w-full rounded-[13px]">
              <Link
                to="/sales"
                state={{ openSaleId: saleByAppointment.id }}
                onClick={() => onOpenChange(false)}
              >
                <FileText className="size-[18px]" />
                {t("appointmentDetails.viewSale")}
              </Link>
            </Button>
          </PermissionGuard>
        ) : null}

        {!isCrossClinicAppointment && !hasActiveSale && canShowPaymentCta ? (
          <PermissionGuard resource="sales" action="create" showAlert={false}>
            <Button
              onClick={handleGoToPayment}
              className="mt-1 h-[48px] w-full rounded-[13px] shadow-lg shadow-primary/30"
            >
              <CreditCard className="size-[18px]" />
              {t("appointmentDetails.makePayment")}
            </Button>
          </PermissionGuard>
        ) : null}
      </div>
    </section>
  );
}
